import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Flex } from 'antd'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'

import { applicationsApplicationsRetrieveOptions } from 'src/api/main'
// eslint-disable-next-line no-restricted-imports -- P2 (86caeu8xd): app roles still on mock until the app-RBAC contract lands
import { mockAppApi, mockQueryKeys } from 'src/api/mock'
import { ForbiddenError, Loading, QueryErrorAlert } from 'src/components'
import { WorkspacePageShell } from 'src/features/workspaces/pages/WorkspacePageShell'
import { useFeatureAppWebhooks } from 'src/flags'
import { useDocumentTitle, useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router'
import { WorkspacePermissionCode } from 'src/utils'

import { AppCredentialsPanel } from '../components/AppCredentialsPanel'
import { AppDetailAuditTab } from '../components/AppDetailAuditTab'
import { AppDetailHero } from '../components/AppDetailHero'
import { AppDetailNav } from '../components/AppDetailNav'
import { AppDetailOverviewTab } from '../components/AppDetailOverviewTab'
import { AppDetailSettingsTab } from '../components/AppDetailSettingsTab'
import { AppGroupsPanel } from '../components/AppGroupsPanel'
import { AppOidcPanel } from '../components/AppOidcPanel'
import { AppOutboundWebhooksPanel } from '../components/AppOutboundWebhooksPanel'
import { AppScimPanel } from '../components/AppScimPanel'
import { AppUsersPanel } from '../components/AppUsersPanel'
import { AppWebhooksPanel } from '../components/AppWebhooksPanel'
import { AppRolesMatrixPanel } from '../components/roles'
import { useWorkspaceAppAccessCounts } from '../hooks/useWorkspaceAppAccessCounts'
import { useWorkspaceAppDetail } from '../hooks/useWorkspaceAppDetail'
import {
  availableSections,
  isAppDetailSection,
  isCrmApp,
  isThirdPartyApp,
  navGroupsFor,
  resolveLegacySection,
  type AppDetailSection
} from '../utils/appDetailNav'
import { mapApplicationToWorkspaceApp } from '../utils/mapApplicationToWorkspaceApp'

/**

 * App sections that exist to configure the application rather than read it —

 * each needs `applications.manage`. Overview, People and Activity stay

 * readable for anyone holding `applications.view`.

 */

const MANAGED_SECTIONS = new Set([
  'groups',

  'roles',

  'oidc',

  'scim',

  'credentials',

  'webhooks',

  'outboundWebhooks',

  'settings'
])

export const WorkspaceAppDetailPage = () => {
  const [canManageApps, canAssignAppAccess, canViewGroups, canViewAudit] =
    useHasWorkspacePermissions([
      WorkspacePermissionCode.APPLICATIONS_MANAGE,
      WorkspacePermissionCode.APPLICATIONS_ACCESS,
      WorkspacePermissionCode.GROUPS_VIEW,
      WorkspacePermissionCode.AUDIT_VIEW
    ])

  // `applications.manage` is not the only code in play. People is the app's
  // access list, which the backend serves and writes on `applications.access`;
  // Groups additionally reads the workspace groups; Activity reads the audit
  // log. Everything else is the app's own configuration.
  const isSectionAllowed = useCallback(
    (section: AppDetailSection) => {
      if (section === 'people') return canAssignAppAccess
      if (section === 'groups') return canViewGroups && canManageApps
      if (section === 'activity') return canViewAudit

      return !MANAGED_SECTIONS.has(section) || canManageApps
    },
    [canAssignAppAccess, canManageApps, canViewAudit, canViewGroups]
  )
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id = '', appId = '' } = useParams<{ id: string; appId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const workspaceId = Number(id)
  // P1 scope gating: webhooks stay hidden until the backend webhook contract is
  // reconciled (P7). When the flag lands it joins the Connection group.
  const webhooksEnabled = useFeatureAppWebhooks()

  const appIdNum = Number(appId)
  const workspaceOk =
    Number.isFinite(workspaceId) &&
    workspaceId > 0 &&
    id !== 'new' &&
    Boolean(appId)

  const apiDetailEnabled =
    workspaceOk && Number.isFinite(appIdNum) && appIdNum > 0

  const {
    data: appRecord,
    isLoading: isLoadingApi,
    isError,
    refetch
  } = useQuery({
    ...applicationsApplicationsRetrieveOptions({
      path: { id: appIdNum }
    }),
    enabled: apiDetailEnabled
  })

  const app = useMemo(
    () =>
      appRecord
        ? mapApplicationToWorkspaceApp(appRecord, workspaceId)
        : undefined,
    [appRecord, workspaceId]
  )

  useDocumentTitle(app?.name)

  const detail = useWorkspaceAppDetail(workspaceId, app?.id)
  const { countsByAppId } = useWorkspaceAppAccessCounts(workspaceId)

  const isThirdParty = isThirdPartyApp(app?.instanceTypeCode)
  const isCrm = isCrmApp(app?.instanceTypeCode)
  const navContext = useMemo(
    () => ({ isThirdParty, webhooksEnabled, isCrm }),
    [isThirdParty, webhooksEnabled, isCrm]
  )
  const navGroups = useMemo(
    () =>
      navGroupsFor(navContext)
        .map((group) => ({
          ...group,
          items: group.items.filter(isSectionAllowed)
        }))
        .filter((group) => group.items.length > 0),
    [navContext, isSectionAllowed]
  )
  const sections = useMemo(() => availableSections(navContext), [navContext])

  const sectionParam = searchParams.get('section') ?? searchParams.get('tab')
  const activeSection: AppDetailSection =
    isAppDetailSection(sectionParam) && sections.includes(sectionParam)
      ? sectionParam
      : 'overview'

  /**
   * Rewrites pre-restructure params (`?tab=users|roles|oidc|…`) onto the
   * canonical `?section=`, and drops anything that no longer resolves.
   */
  useEffect(() => {
    const legacy = searchParams.get('tab')
    if (!legacy) {
      return
    }
    const next = new URLSearchParams(searchParams)
    next.delete('tab')
    next.delete('seg')
    const resolved =
      resolveLegacySection(searchParams.get('seg')) ??
      resolveLegacySection(legacy)
    if (resolved && sections.includes(resolved) && resolved !== 'overview') {
      next.set('section', resolved)
    }
    setSearchParams(next, { replace: true })
  }, [searchParams, sections, setSearchParams])

  useEffect(() => {
    if (!app) {
      return
    }
    void mockAppApi
      .ensureAppMockBundle(app.id, workspaceId, app.name)
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: mockQueryKeys.appRoles(app.id)
        })
      )
  }, [app, queryClient, workspaceId])

  const setSection = useCallback(
    (section: AppDetailSection) => {
      const next = new URLSearchParams(searchParams)
      if (section === 'overview') {
        next.delete('section')
      } else {
        next.set('section', section)
      }
      // `audit_tab` only means something inside Activity — leaving it behind
      // strands a dead param on every other section's URL.
      if (section !== 'activity') {
        next.delete('audit_tab')
      }
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  if (!id || id === 'new' || !Number.isFinite(workspaceId)) {
    return <Navigate to={paths.workspaces.root} replace />
  }

  if (!workspaceOk) {
    return <Navigate to={paths.workspaces.apps.root(id)} replace />
  }

  if (isError && !isLoadingApi) {
    return (
      <WorkspacePageShell
        sectionTitle={t('workspace.apps.title')}
        sectionDescription={t('workspace.apps.description')}
      >
        <QueryErrorAlert onRetry={() => void refetch()} />
      </WorkspacePageShell>
    )
  }

  if (isLoadingApi && !app) {
    return <Loading />
  }

  if (!app) {
    return <Navigate to={paths.workspaces.apps.root(id)} replace />
  }

  const appIdNumeric = Number(app.id) || 0

  return (
    <WorkspacePageShell
      breadcrumbLeaf={app.name}
      breadcrumbExtras={[
        {
          title: t('shell.menu.apps'),
          href: paths.workspaces.apps.root(id)
        }
      ]}
    >
      <AppDetailHero app={app} />

      <Flex gap={16} align="flex-start" wrap>
        <div style={{ flex: '0 0 200px', maxWidth: 200, minWidth: 0 }}>
          <AppDetailNav
            groups={navGroups}
            active={activeSection}
            onSelect={setSection}
            counts={{
              people: countsByAppId.get(appIdNumeric),
              roles: app.relatedRoles?.length
            }}
          />
        </div>

        <div style={{ flex: '1 1 340px', minWidth: 0 }}>
          {!isSectionAllowed(activeSection) ? (
            <ForbiddenError fallbackPath={paths.workspaces.apps.root(id)} />
          ) : (
            <>
              {activeSection === 'overview' && (
                <AppDetailOverviewTab app={app} />
              )}

              {activeSection === 'people' && (
                <AppUsersPanel
                  applicationId={appIdNumeric}
                  organizationId={workspaceId}
                  relatedRoles={app.relatedRoles}
                />
              )}
              {activeSection === 'groups' && (
                <AppGroupsPanel
                  applicationId={appIdNumeric}
                  organizationId={workspaceId}
                />
              )}
              {activeSection === 'roles' && (
                <AppRolesMatrixPanel
                  applicationId={appIdNumeric}
                  applicationType={app.instanceTypeCode}
                  applicationRepositoryId={app.instanceTypeId}
                  organizationId={workspaceId}
                />
              )}

              {activeSection === 'oidc' && (
                <AppOidcPanel
                  applicationId={appIdNumeric}
                  applicationName={app.name}
                  applicationToken={app.token}
                  applicationType={app.instanceTypeCode ?? ''}
                  organizationId={workspaceId}
                />
              )}
              {activeSection === 'scim' && (
                <AppScimPanel
                  applicationId={appIdNumeric}
                  applicationType={app.instanceTypeCode ?? ''}
                />
              )}
              {activeSection === 'credentials' && (
                <AppCredentialsPanel app={app} detail={detail} />
              )}
              {activeSection === 'webhooks' && (
                <AppWebhooksPanel applicationId={appIdNumeric} />
              )}
              {activeSection === 'outboundWebhooks' && (
                <AppOutboundWebhooksPanel app={app} />
              )}

              {activeSection === 'activity' && (
                <AppDetailAuditTab
                  applicationId={appIdNumeric}
                  organizationId={workspaceId}
                />
              )}

              {activeSection === 'settings' && (
                <AppDetailSettingsTab
                  app={app}
                  workspaceId={workspaceId}
                  onDeleted={() => navigate(paths.workspaces.apps.root(id))}
                />
              )}
            </>
          )}
        </div>
      </Flex>
    </WorkspacePageShell>
  )
}
